import { useTranslation } from "react-i18next";
import { ArrowLeft, ArrowRight } from "@untitledui/icons";
import { Button } from "@/components/base/buttons/button";
import type { Page } from "@/lib/page";

/**
 * Lišta stránkování pod tabulkou: „Stránka X z Y · N záznamů" + První/Předchozí/
 * Další/Poslední. Sdílená všemi seznamy stránkovanými serverem — dřív byla tatáž
 * lišta zkopírovaná v transakčních logech i auditu a texty se braly z klíčů `txlogs.*`,
 * i když s transakčními logy nesouvisely.
 *
 * Komponenta jen zobrazuje; stav stránky drží volající (a resetuje ho při změně filtru).
 */
export function Pager<T>({
  page,
  onPage,
  isFetching = false,
}: {
  /** Odpověď serveru — bere se z ní číslo stránky, počty i krajní meze. */
  page: Pick<Page<T>, "number" | "totalPages" | "totalElements" | "first" | "last">;
  onPage: (page: number) => void;
  /** Během načítání se tlačítka zamknou, ať se nedá překliknout přes běžící dotaz. */
  isFetching?: boolean;
}) {
  const { t } = useTranslation();
  const lastIndex = Math.max(0, page.totalPages - 1);

  return (
    <div className="flex items-center justify-between">
      <p className="text-sm text-tertiary">
        {t("pager.pageInfo", {
          page: page.number + 1,
          pages: Math.max(page.totalPages, 1),
          total: page.totalElements,
        })}
      </p>
      <div className="flex gap-2">
        <Button size="sm" color="secondary" isDisabled={page.first || isFetching} onPress={() => onPage(0)}>
          {t("pager.first")}
        </Button>
        <Button
          size="sm"
          color="secondary"
          iconLeading={ArrowLeft}
          isDisabled={page.first || isFetching}
          onPress={() => onPage(Math.max(0, page.number - 1))}
        >
          {t("pager.prev")}
        </Button>
        <Button
          size="sm"
          color="secondary"
          iconTrailing={ArrowRight}
          isDisabled={page.last || isFetching}
          onPress={() => onPage(page.number + 1)}
        >
          {t("pager.next")}
        </Button>
        <Button size="sm" color="secondary" isDisabled={page.last || isFetching} onPress={() => onPage(lastIndex)}>
          {t("pager.last")}
        </Button>
      </div>
    </div>
  );
}
